/*!
 * EndlessPianoExport — offline export of the endless-piano generative stream.
 *
 * One deterministic timeline (EndlessPianoEngine.buildTimeline, seeded) feeds
 * three output paths:
 *   MIDI — SMF format 0 from the beat domain (quantized, per-bar tempo map
 *          reproduces the composer's ritenuti), kilobytes, instant;
 *   WAV  — the full instrument (sampler + synth fallback + reverb chain from
 *          piano-synth) rebuilt inside an OfflineAudioContext, rendered faster
 *          than real time, 16-bit PCM;
 *   MP3  — the same render encoded via the shared MP3Encoder worker (lamejs),
 *          with ID3 tags incl. the Salamander CC BY attribution comment.
 *
 * The SMF/WAV writers and duration math are pure — Node-importable for tests.
 * ES5-safe.
 */
(function (root) {
'use strict';

var PPQ = 480;              /* MIDI ticks per quarter note */
var SR = 44100;             /* offline render sample rate (lamejs-supported) */
var FADE_SEC = 3;           /* gentle fade at the end of audio exports */
var NORM_TARGET = 0.95;     /* peak-normalize target for audio exports */

/* ============================== */
/* SMF (MIDI) WRITER — pure       */
/* ============================== */

/* variable-length quantity (delta times) */
function vlq(n) {
    if (n < 0) n = 0;
    var out = [n & 0x7F];
    n >>>= 7;
    while (n > 0) { out.unshift((n & 0x7F) | 0x80); n >>>= 7; }
    return out;
}

/*
 * timeline → SMF format-0 Uint8Array.
 * Beat domain only (startBeat/durBeats) — the humanize jitter stays in the
 * audio path; a DAW wants quantized notes. The per-bar tempo events carry
 * the composer's ritenuti so playback still breathes like the live stream.
 */
function writeMidi(timeline) {
    var evs = [];   /* {tick, ord, bytes} — ord: tempo 0 < off 1 < on 2 */
    var lastUsPerQ = 0;
    var barTicks = 4 * PPQ;

    for (var b = 0; b < timeline.bars.length; b++) {
        var bar = timeline.bars[b];
        var barTick = b * barTicks;
        var usPerQ = Math.round(60000000 / timeline.bpm * bar.ritMul);
        if (usPerQ !== lastUsPerQ) {
            lastUsPerQ = usPerQ;
            evs.push({ tick: barTick, ord: 0, bytes: [0xFF, 0x51, 0x03,
                (usPerQ >> 16) & 0xFF, (usPerQ >> 8) & 0xFF, usPerQ & 0xFF] });
        }
        for (var i = 0; i < bar.events.length; i++) {
            var ev = bar.events[i];
            var on = barTick + Math.round(ev.startBeat * PPQ);
            var off = on + Math.max(1, Math.round(ev.durBeats * PPQ));
            var vel = Math.max(1, Math.min(127, Math.round(ev.vel * 127)));
            evs.push({ tick: on, ord: 2, bytes: [0x90, ev.midi & 0x7F, vel] });
            evs.push({ tick: off, ord: 1, bytes: [0x80, ev.midi & 0x7F, 0x40] });
        }
    }
    evs.sort(function (a, b2) { return a.tick - b2.tick || a.ord - b2.ord; });

    var track = [];
    /* program change: acoustic grand on channel 0 */
    track.push(0x00, 0xC0, 0x00);
    var lastTick = 0;
    for (var k = 0; k < evs.length; k++) {
        var d = vlq(evs[k].tick - lastTick);
        lastTick = evs[k].tick;
        for (var j = 0; j < d.length; j++) track.push(d[j]);
        for (var j2 = 0; j2 < evs[k].bytes.length; j2++) track.push(evs[k].bytes[j2]);
    }
    track.push(0x00, 0xFF, 0x2F, 0x00);   /* end of track */

    var out = new Uint8Array(14 + 8 + track.length);
    var p = 0;
    function put(bytes) { for (var q = 0; q < bytes.length; q++) out[p++] = bytes[q]; }
    function u32(n) { put([(n >>> 24) & 0xFF, (n >>> 16) & 0xFF, (n >>> 8) & 0xFF, n & 0xFF]); }
    put([0x4D, 0x54, 0x68, 0x64]); u32(6);
    put([0x00, 0x00, 0x00, 0x01, (PPQ >> 8) & 0xFF, PPQ & 0xFF]);   /* fmt 0, 1 trk */
    put([0x4D, 0x54, 0x72, 0x6B]); u32(track.length);
    put(track);
    return out;
}

/* ============================== */
/* WAV WRITER — pure              */
/* ============================== */
/* channels: array of Float32Array (1..2), equal length → 16-bit PCM RIFF */
function writeWavBytes(channels, sampleRate) {
    var nCh = channels.length, len = channels[0].length;
    var blockAlign = nCh * 2;
    var dataBytes = len * blockAlign;
    var ab = new ArrayBuffer(44 + dataBytes);
    var dv = new DataView(ab);
    function str(off, s) { for (var i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i)); }
    str(0, 'RIFF'); dv.setUint32(4, 36 + dataBytes, true); str(8, 'WAVE');
    str(12, 'fmt '); dv.setUint32(16, 16, true);
    dv.setUint16(20, 1, true);                       /* PCM */
    dv.setUint16(22, nCh, true);
    dv.setUint32(24, sampleRate, true);
    dv.setUint32(28, sampleRate * blockAlign, true);
    dv.setUint16(32, blockAlign, true);
    dv.setUint16(34, 16, true);
    str(36, 'data'); dv.setUint32(40, dataBytes, true);
    var off = 44;
    for (var i = 0; i < len; i++) {
        for (var c = 0; c < nCh; c++) {
            var s = channels[c][i];
            if (s > 1) s = 1; else if (s < -1) s = -1;
            dv.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
            off += 2;
        }
    }
    return ab;
}

/* ============================== */
/* OFFLINE RENDER (browser only)  */
/* ============================== */

function loadSamplesInto(sampler, base, ver, timeoutMs) {
    return new Promise(function (resolve) {
        var total = root.EndlessPianoEngine.SAMPLE_NOTES.length;
        sampler.load(base, ver);
        var t0 = Date.now();
        var iv = setInterval(function () {
            if (sampler.loadedCount + sampler.failedCount >= total ||
                Date.now() - t0 > timeoutMs) {
                clearInterval(iv);
                resolve();
            }
        }, 150);
    });
}

/* peak-normalize in place; attenuate always, boost at most ×3 */
function normalize(buf) {
    var peak = 0, c, i, d;
    for (c = 0; c < buf.numberOfChannels; c++) {
        d = buf.getChannelData(c);
        for (i = 0; i < d.length; i++) {
            var a = d[i] < 0 ? -d[i] : d[i];
            if (a > peak) peak = a;
        }
    }
    if (peak < 0.0001) return buf;
    var gain = NORM_TARGET / peak;
    if (gain > 3) gain = 3;
    if (gain > 0.98 && gain < 1.02) return buf;
    for (c = 0; c < buf.numberOfChannels; c++) {
        d = buf.getChannelData(c);
        for (i = 0; i < d.length; i++) d[i] *= gain;
    }
    return buf;
}

/*
 * Rebuild the live instrument inside an OfflineAudioContext and render the
 * timeline. opts: { reverb, targetSec, sampleBase, sampleVersion, buffers
 * (pre-decoded {midi→AudioBuffer} from the live sampler — skips the fetch),
 * onProgress(0..1) }. Resolves to a normalized AudioBuffer.
 */
function renderAudio(timeline, opts) {
    var OC = root.OfflineAudioContext || root.webkitOfflineAudioContext;
    if (!OC) return Promise.reject(new Error('OfflineAudioContext unavailable'));
    var targetSec = opts.targetSec;
    var octx = new OC(2, Math.ceil(targetSec * SR), SR);

    var synth = root.TimbricaPianoSynth.create({ voice: 'grand', reverb: opts.reverb || 'warm', ctx: octx });
    synth.init();
    if (!synth.ctx) return Promise.reject(new Error('synth init failed'));

    var sampler = root.EndlessPianoEngine.createSampler(octx,
        synth.getSampleBus ? synth.getSampleBus() : synth.getInputBus(), {});
    var prep;
    if (opts.buffers) {
        sampler.buffers = opts.buffers;
        prep = Promise.resolve();
    } else if (opts.sampleBase) {
        prep = loadSamplesInto(sampler, opts.sampleBase, opts.sampleVersion, 45000);
    } else {
        prep = Promise.resolve();
    }

    return prep.then(function () {
        var cutoff = targetSec - 0.05;
        for (var b = 0; b < timeline.bars.length; b++) {
            var bar = timeline.bars[b];
            for (var i = 0; i < bar.events.length; i++) {
                var ev = bar.events[i];
                if (ev.when >= cutoff) continue;
                if (sampler.ready(ev.midi)) {
                    sampler.noteOn(ev.midi, ev.vel, ev.when, ev.dur);
                } else {
                    synth.noteOn(ev.midi, ev.vel, ev.when);
                    synth.noteOff(ev.midi, ev.when + ev.dur);
                }
            }
        }
        /* the sleep-timer language: fade the master bus over the last seconds */
        var master = synth.getInputBus();
        var fadeStart = Math.max(0, targetSec - FADE_SEC);
        master.gain.setValueAtTime(master.gain.value, fadeStart);
        master.gain.linearRampToValueAtTime(0.0001, cutoff);

        /* render progress via suspend checkpoints (best effort — not every
           browser exposes OfflineAudioContext.suspend) */
        if (opts.onProgress && typeof octx.suspend === 'function') {
            var step = Math.max(5, targetSec / 20);
            for (var t = step; t < targetSec - 1; t += step) {
                (function (at) {
                    try {
                        octx.suspend(at).then(function () {
                            try { opts.onProgress(at / targetSec); } catch (e) {}
                            octx.resume();
                        }).catch(function () {});
                    } catch (e) {}
                })(t);
            }
        }
        return octx.startRendering();
    }).then(function (rendered) {
        return normalize(rendered);
    });
}

/* ============================== */
/* ORCHESTRATOR (browser only)    */
/* ============================== */
/*
 * opts: {
 *   format: 'midi'|'wav'|'mp3', minutes, mood, bpm, octaves, reverb,
 *   sampleBase, sampleVersion, buffers,      — audio formats only
 *   id3: { title, artist, album, ... },      — mp3 only
 *   onStage: function(stage, pct)            — 'compose'|'render'|'encode'
 * }
 * Resolves { blob, filename, seed }.
 */
function run(opts) {
    var stage = opts.onStage || function () {};
    var seed = Date.now();
    var targetSec = opts.minutes * 60;
    stage('compose', 0);
    return Promise.resolve().then(function () {
        var timeline = root.EndlessPianoEngine.buildTimeline(
            opts.mood, opts.bpm, opts.octaves, seed, targetSec);
        var tag = seed.toString(36).slice(-4);
        var base = 'endless-piano-' + opts.mood + '-' + opts.minutes + 'min-' + tag;

        if (opts.format === 'midi') {
            var bytes = writeMidi(timeline);
            return { blob: new Blob([bytes], { type: 'audio/midi' }), filename: base + '.mid', seed: seed };
        }

        stage('render', 0);
        return renderAudio(timeline, {
            reverb: opts.reverb,
            targetSec: targetSec,
            sampleBase: opts.sampleBase,
            sampleVersion: opts.sampleVersion,
            buffers: opts.buffers,
            onProgress: function (p) { stage('render', p); }
        }).then(function (buf) {
            stage('encode', 0);
            if (opts.format === 'wav') {
                var chs = [new Float32Array(buf.getChannelData(0)),
                           new Float32Array(buf.getChannelData(1))];
                var ab = writeWavBytes(chs, buf.sampleRate);
                return { blob: new Blob([ab], { type: 'audio/wav' }), filename: base + '.wav', seed: seed };
            }
            return root.MP3Encoder.encode(buf, {
                bitrate: opts.kbps || 320,   // формат ВЫДАЧИ: число даёт страница из config/formats.php (delivery_kbps)
                slug: 'endless-piano',
                id3: opts.id3 || null,
                onProgress: function (p) { stage('encode', (p || 0) / 100); }
            }).then(function (mp3) {
                return { blob: mp3, filename: base + '.mp3', seed: seed };
            });
        });
    });
}

var api = {
    writeMidi: writeMidi,
    writeWavBytes: writeWavBytes,
    renderAudio: renderAudio,
    run: run,
    PPQ: PPQ
};

if (typeof module !== 'undefined' && module.exports) module.exports = api;
else root.EndlessPianoExport = api;

})(typeof window !== 'undefined' ? window : globalThis);
